/**
 * Media resolution for business profile — no broken images.
 * Single source for default assets; use these helpers everywhere business media is rendered.
 *
 * Fallback chain:
 *  1. Business's own URL (cover_image_url, logo_url, etc.)
 *  2. Category preset image (deterministic by business ID)
 *  3. Default SVG placeholder
 */
import { getCategoryPresetImage } from "./categoryPresetImages";

/** Base path for default business media (in public/) */
const DEFAULTS_BASE = "/images/defaults";

/** Default logo — neutral placeholder */
export const DEFAULT_BUSINESS_LOGO = `${DEFAULTS_BASE}/default-business-logo.svg`;

/** Default cover image */
export const DEFAULT_BUSINESS_COVER = `${DEFAULTS_BASE}/default-business-cover.svg`;

/** Default gallery placeholders (3–4 for carousels) */
export const DEFAULT_GALLERY = [
  `${DEFAULTS_BASE}/default-gallery-1.svg`,
  `${DEFAULTS_BASE}/default-gallery-2.svg`,
  `${DEFAULTS_BASE}/default-gallery-3.svg`,
  `${DEFAULTS_BASE}/default-gallery-4.svg`,
];

/**
 * Check if URL is valid and non-empty.
 * @param {string|null|undefined} url
 * @returns {boolean}
 */
function isValidUrl(url) {
  if (url == null || typeof url !== "string") return false;
  const t = url.trim();
  if (t.length === 0) return false;
  if (t.startsWith("data:")) return true;
  if (t.startsWith("http://") || t.startsWith("https://") || t.startsWith("/")) return true;
  return false;
}

/**
 * Downsize remote image URLs to a sane rendered size. Google Places CDN
 * (lh3.googleusercontent.com) returns URLs like `...=s4800-w800` which download
 * the full 800px image even when we render at 300px — wasteful on mobile.
 * Unsplash exposes `?w=` query params that get transformed server-side.
 */
function downsizeRemoteImage(url, targetWidth = 600) {
  if (!url || typeof url !== "string") return url;
  // Google user content: =sNNNN-wNNNN or =wNNNN-hNNNN suffix
  if (/googleusercontent\.com\//.test(url)) {
    return url.replace(/=[swh]\d+(-[swh]\d+)*(-[a-z])*$/i, `=w${targetWidth}-h${Math.round(targetWidth * 0.75)}-p-k-no`);
  }
  // Unsplash: enforce w= and q=
  if (/images\.unsplash\.com\//.test(url)) {
    const u = new URL(url);
    u.searchParams.set("w", String(targetWidth));
    u.searchParams.set("q", "75");
    u.searchParams.set("auto", "format");
    return u.toString();
  }
  return url;
}

export { downsizeRemoteImage };

/**
 * Resolve business logo URL. Falls back to default if empty/broken.
 * @param {object} business — normalized business (banner, img, entity?.branding?.logoUrl, etc.)
 * @returns {string} URL to use in img src
 */
export function resolveBusinessLogo(business, targetWidth = 160) {
  const candidates = [
    business?.img,
    business?.logoUrl,
    business?.logo_url,
    business?.entity?.branding?.logoUrl,
    business?.settings?.publicProfile?.img,
    business?.settings?.publicProfile?.logo,
  ];
  for (const u of candidates) {
    if (isValidUrl(u)) return downsizeRemoteImage(u, targetWidth);
  }
  return DEFAULT_BUSINESS_LOGO;
}

/**
 * Resolve business cover/banner URL with category preset fallback.
 * @param {object} business — must have at least { id }; optionally { categorySlug, category }
 * @returns {string} URL to use in img src
 */
export function resolveBusinessCover(business, targetWidth = 600) {
  const candidates = [
    business?.resolvedCoverImage,
    business?.coverImage,
    business?.cover_image,
    business?.cover_image_url,
    business?.banner,
    business?.entity?.branding?.coverImageUrl,
    business?.settings?.publicProfile?.banner,
  ];
  for (const u of candidates) {
    if (isValidUrl(u)) return downsizeRemoteImage(u, targetWidth);
  }
  // Category preset fallback — deterministic by business ID
  const categoryHint = business?.categorySlug || business?.category_slug || business?.category || null;
  const businessId = business?.id || 0;
  if (categoryHint || businessId) {
    return getCategoryPresetImage(categoryHint, businessId);
  }
  return DEFAULT_BUSINESS_COVER;
}

/**
 * Resolve gallery images. Returns array; uses category presets or placeholders if empty.
 * @param {object} business — may have gallery, galleryImages, photos, entity?.branding?.gallery
 * @param {number} minCount — minimum placeholders to return when gallery is empty
 * @returns {string[]}
 */
export function resolveBusinessGallery(business, minCount = 4) {
  const raw =
    business?.galleryImages ??
    business?.gallery ??
    business?.entity?.branding?.gallery ??
    business?.settings?.publicProfile?.gallery;
  let urls = [];
  if (Array.isArray(raw)) {
    urls = raw.map((u) => (typeof u === "string" ? u : u?.url)).filter(isValidUrl);
  }
  if (urls.length >= minCount) return urls;
  // Use photos array if available
  if (Array.isArray(business?.photos) && business.photos.length > 0) {
    const photoUrls = business.photos.map((p) => p?.url || p?.thumbUrl).filter(isValidUrl);
    if (photoUrls.length >= minCount) return photoUrls;
    urls = [...urls, ...photoUrls];
  }
  if (urls.length >= minCount) return urls.slice(0, minCount);
  const placeholders = DEFAULT_GALLERY.slice(0, Math.max(minCount - urls.length, 1));
  return urls.length > 0 ? urls : placeholders;
}

/**
 * onError handler for <img> tags — replaces broken image with category preset or default.
 * Usage: <img src={url} onError={handleImageError(business)} />
 * @param {object} business — for category preset lookup
 * @param {'cover'|'logo'} type — which fallback to use
 * @returns {function} event handler
 */
export function handleImageError(business, type = "cover") {
  return (e) => {
    // Prevent infinite loop
    if (e.target.dataset.fallbackApplied) return;
    e.target.dataset.fallbackApplied = "true";

    if (type === "logo") {
      e.target.src = DEFAULT_BUSINESS_LOGO;
    } else {
      // Try category preset first
      const categoryHint = business?.categorySlug || business?.category_slug || business?.category || null;
      const businessId = business?.id || 0;
      if (categoryHint || businessId) {
        e.target.src = getCategoryPresetImage(categoryHint, businessId);
      } else {
        e.target.src = DEFAULT_BUSINESS_COVER;
      }
    }
  };
}

export { isValidUrl };
